import {
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import { EyeIcon } from "@heroicons/react/24/solid";
import { format } from "date-fns";
import { useState } from "react";
import { Link } from "react-router-dom";
import SessionModal from "../../components/modals/SessionsModal";
import { ProfilePictureAvatar } from "../../components/taxi/Profile_picture_avatar";
import { DriverProfileStatusDisplayValue } from "../../helpers/enums";
import { Chip, formatDateString, pluralize } from "../../helpers/general";
import { tdStyle, thStyle } from "../../helpers/constants";

interface SimpleDriversTableProps {
  drivers: Driver[];
  sortBy: "default" | "sessionsCount";
  onSortChange?: (sortField: "default" | "sessionsCount") => void;
  date: string;
  range: string;
}

const statusTone = (status: string) =>
  status === "APPROVED"
    ? "success"
    : status === "PENDING"
      ? "warning"
      : "neutral";

export default function SimpleDriversTable({
  drivers,
  sortBy,
  onSortChange = () => {},
  date,
  range,
}: SimpleDriversTableProps) {
  const [selectedDriverSessions, setSelectedDriverSessions] =
    useState<Driver | null>(null);

  const handleSortToggle = () =>
    onSortChange(sortBy === "sessionsCount" ? "default" : "sessionsCount");

  return (
    <>
      <CTable responsive hover align="middle" className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell scope="col" style={thStyle}>
              Chauffeur
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Statut
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Dernière connexion
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              <button
                type="button"
                title="Trier par nombre de connexions"
                className="border-0 bg-transparent p-0"
                style={{
                  font: "inherit",
                  color: "inherit",
                  letterSpacing: "inherit",
                  textTransform: "inherit",
                }}
                onClick={handleSortToggle}
              >
                Sessions {sortBy === "sessionsCount" ? "▲" : "▼"}
              </button>
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle} />
          </CTableRow>
        </CTableHead>

        <CTableBody>
          {drivers.map((driver) => {
            const statusKey = driver.status?.toUpperCase();
            const sessionsCount = driver.sessions?.length ?? 0;
            const last =
              sessionsCount > 0 ? driver.sessions[0].createdAt : null;

            return (
              <CTableRow key={driver.id}>
                <CTableDataCell style={tdStyle}>
                  <div
                    className="d-flex align-items-center gap-2"
                    style={{ minWidth: 200 }}
                  >
                    <ProfilePictureAvatar
                      profilePicture={driver?.profilePicture}
                      fullName={driver?.fullName}
                      isOnline={driver?.isOnline}
                    />
                    <div style={{ minWidth: 0 }}>
                      <Link
                        to={`/drivers/${driver?.id}`}
                        className="fw-semibold text-capitalize d-block"
                      >
                        {driver?.fullName}
                      </Link>
                      <div
                        className="text-muted text-truncate"
                        style={{ fontSize: 12, maxWidth: 240 }}
                      >
                        {driver.email}
                      </div>
                    </div>
                  </div>
                </CTableDataCell>

                <CTableDataCell style={tdStyle}>
                  <Chip tone={statusTone(statusKey)}>
                    {
                      DriverProfileStatusDisplayValue[
                        statusKey as keyof typeof DriverProfileStatusDisplayValue
                      ]
                    }
                  </Chip>
                </CTableDataCell>

                <CTableDataCell style={tdStyle}>
                  {last ? (
                    <>
                      <div className="fw-semibold">
                        {formatDateString(last)}
                      </div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        {format(new Date(last), "HH:mm")}
                      </div>
                    </>
                  ) : (
                    <span className="text-muted">Aucune connexion</span>
                  )}
                </CTableDataCell>

                <CTableDataCell style={tdStyle}>
                  {sessionsCount > 0 ? (
                    <Chip tone="success">
                      {sessionsCount} {pluralize("Session", sessionsCount)}
                    </Chip>
                  ) : (
                    <Chip tone="danger">Aucun login</Chip>
                  )}
                </CTableDataCell>

                <CTableDataCell style={tdStyle}>
                  <div className="d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-sm rounded-pill border px-3"
                      title="Voir les sessions"
                      onClick={() => setSelectedDriverSessions(driver)}
                    >
                      <EyeIcon width={16} height={16} />
                    </button>
                  </div>
                </CTableDataCell>
              </CTableRow>
            );
          })}
        </CTableBody>
      </CTable>

      {selectedDriverSessions && (
        <SessionModal
          driver={selectedDriverSessions}
          onClose={() => setSelectedDriverSessions(null)}
          date={date}
          range={range}
        />
      )}
    </>
  );
}
